import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';
import OnboardingModal from '../components/OnboardingModal.jsx';
import { APP_VERSION } from '../version.js';

const THEME_ICONS = {
  espace: '🚀', geographie: '🌍', mythologie: '⚡', 'corps-humain': '🫀',
  histoire: '⏳', animaux: '🦁', personnages: '👤',
  sport: '⚽', inventions: '💡', saveurs: '🌶️',
};

const DIFF_LABELS = {
  decouverte: '🐣 Découverte',
  curieux: '🔍 Curieux',
  explorateur: '🧭 Explorateur',
  expert: '⭐ Expert',
};

export default function Home() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [stats, setStats] = useState([]);
  const [missions, setMissions] = useState([]);
  const [missionsOpen, setMissionsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadConns, setUnreadConns] = useState(0);
  const [connProgress, setConnProgress] = useState(null);
  const [connExpanded, setConnExpanded] = useState(false);
  // Onboarding d'accueil en 2 écrans (1 = Arbre/Connexions, 2 = site web + réglages).
  // Deux écrans courts plutôt qu'un long qui scrolle : la partie "site web" était en
  // bas du contenu et passait à la trappe.
  const [tipStep, setTipStep] = useState(1);

  useEffect(() => {
    api.getMe().then(u => { setUser(u); }).catch(() => {});
    Promise.all([api.getThemes(), api.getStats(), api.getTodayMissions(), api.getUnreadConnections(), api.getConnectionProgress()])
      .then(([t, s, m, uc, cp]) => { setThemes(t); setStats(s); setMissions(m.missions || []); setUnreadConns(uc.unread || 0); setConnProgress(cp); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const startQuiz = async (themeId) => {
    try {
      const session = await api.startQuiz(themeId);
      navigate(`/quiz/${session.id}`, { state: { session } });
    } catch (err) {
      alert(err.message);
    }
  };

  const dismissTimerTip = async () => {
    setUser({ ...user, seen_timer_tip: true });
    try { await api.updateProfile({ seen_timer_tip: true }); } catch (e) { /* non bloquant */ }
  };

  const totalCards = stats.reduce((s, t) => s + t.cards_unlocked, 0);
  const totalAvailable = stats.reduce((s, t) => s + t.total_cards, 0);
  const totalAnswered = stats.reduce((s, t) => s + t.answered, 0);
  const totalCorrect = stats.reduce((s, t) => s + t.correct, 0);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar unreadConns={unreadConns} />

      {user && user.seen_timer_tip === false && tipStep === 1 && (
        <OnboardingModal
          emoji="🍃"
          title="Bienvenue dans Curio !"
          buttonLabel="Suivant →"
          onClose={() => setTipStep(2)}
        >
          Chaque bonne réponse te fait faire une <strong>nouvelle découverte</strong> et fait <strong>pousser une feuille sur ton Arbre</strong> 🌳. Plus tu joues, plus ton arbre se remplit de tout ce que tu sais — <strong>ton savoir à toi</strong>, qui ne fait que grandir.
          <br /><br />
          Et le plus chouette : <strong>deux choses apprises dans des thèmes différents peuvent se rejoindre</strong> — c'est une <strong>Connexion</strong> 🔗 ! Par exemple, tu apprends que le <strong>requin</strong> n'a pas d'os mais du <strong>cartilage</strong>, cette matière souple… puis que <strong>ton nez et tes oreilles</strong> en sont faits aussi ! Curio les relie : <strong>le requin et ton nez sont faits de la même chose</strong> ! 🤯
        </OnboardingModal>
      )}

      {user && user.seen_timer_tip === false && tipStep === 2 && (
        <OnboardingModal
          emoji="🌐"
          title="Sur tous tes écrans"
          buttonLabel="C'est parti ! 🚀"
          onClose={dismissTimerTip}
        >
          Curiosaurus se joue sur <strong>mobile</strong> et sur <strong>navigateur</strong>, sur <strong>https://curiosaurus.app</strong> — avec le <strong>même compte</strong>. Ta progression, ton arbre et tes connexions te suivent d'un appareil à l'autre.
          <br /><br />
          💡 <strong>Astuce</strong> : tu peux activer la <strong>lecture à voix haute</strong> 🔊 et <strong>activer ou désactiver le compte à rebours</strong> ⏱️ quand tu veux, dans ton <strong>Profil</strong>.
        </OnboardingModal>
      )}

      <div className="container">
        <div className="home-hero">
          <h1>Salut {user.display_name} ! 👋</h1>
          <p>Choisis un thème et nourris ta curiosité</p>
        </div>

        {!loading && totalAnswered > 0 && (
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-value">{totalAnswered}</div>
              <div className="stat-label">Questions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{totalAnswered > 0 ? Math.round(totalCorrect / totalAnswered * 100) : 0}%</div>
              <div className="stat-label">Réussite</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{totalCards}/{totalAvailable}</div>
              <div className="stat-label">Fiches</div>
            </div>
          </div>
        )}

        {/* Daily Missions */}
        {!loading && missions.length > 0 && (() => {
          const doneCount = missions.filter(m => m.is_completed).length;
          const allDone = doneCount === missions.length;
          return (
          <div className="missions-bar">
            <div
              className="missions-bar-title missions-bar-toggle"
              onClick={() => setMissionsOpen(o => !o)}
              role="button" tabIndex={0}
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}
            >
              <span>🎯 Missions du jour</span>
              <span className={`missions-count ${allDone ? 'all-done' : ''}`}
                    style={{ marginLeft: 'auto', fontWeight: 700, color: allDone ? 'var(--success, #51cf66)' : 'var(--text-muted)' }}>
                {doneCount}/{missions.length}{allDone ? ' ✓' : ''}
              </span>
              <span className="missions-chevron" style={{ transition: 'transform 0.2s', transform: missionsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
            </div>
            {missionsOpen && (
            <div className="missions-list">
              {missions.map(m => {
                const pct = Math.min(100, Math.round(m.progress / m.target * 100));
                return (
                  <div key={m.id} className={`mission-chip ${m.is_completed ? 'completed' : ''}`}>
                    <span className="mission-icon">{m.icon}</span>
                    <div className="mission-info">
                      <span className="mission-title">{m.title}</span>
                      <span className="mission-desc">{m.description}</span>
                    </div>
                    <div className="mission-progress-ring">
                      {m.is_completed ? (
                        <span className="mission-check">✅</span>
                      ) : (
                        <span className="mission-pct">{m.progress}/{m.target}</span>
                      )}
                    </div>
                    <span className="mission-bonus">+{m.bonus_points} pts</span>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })()}

        {!loading && connProgress && (
          <div className="conn-card">
            <div className="conn-card-head">
              <span className="conn-card-title">🔗 Tes connexions</span>
              <span className="conn-card-count">{connProgress.discovered}<span className="conn-card-total"> / {connProgress.total}</span></span>
            </div>
            <div className="conn-bar"><span style={{ width: `${connProgress.total ? Math.round(connProgress.discovered / connProgress.total * 100) : 0}%` }} /></div>
            {connProgress.reachable_total > 0 && (
              <>
                <div className="conn-nudge">
                  💡 <strong>{connProgress.reachable_total} connexion{connProgress.reachable_total > 1 ? 's' : ''}</strong> {connProgress.reachable_total > 1 ? "n'attendent" : "n'attend"} qu'une bonne réponse — joue :
                </div>
                {(() => {
                  // Les thèmes les plus « rentables » d'abord ; on n'en montre que 2,
                  // le reste se déplie pour ne pas faire un pavé géant.
                  const sorted = [...connProgress.reachable_by_theme].sort((a, b) => b.count - a.count);
                  const shown = connExpanded ? sorted : sorted.slice(0, 2);
                  const rest = sorted.length - 2;
                  return (
                    <>
                      <div className="conn-chips">
                        {shown.map(rt => {
                          const th = themes.find(t => t.slug === rt.theme_slug);
                          const locked = th && th.locked && !th.user_unlocked;
                          return (
                            <button key={rt.theme_slug} className="conn-chip"
                              onClick={() => th ? (locked ? navigate('/unlock') : startQuiz(th.id)) : null}>
                              <span className="conn-chip-dot" style={{ background: rt.theme_color }} />
                              {rt.theme_name} <span className="conn-chip-n">{rt.count}</span>
                            </button>
                          );
                        })}
                      </div>
                      {rest > 0 && (
                        <button className="conn-more" onClick={() => setConnExpanded(e => !e)}>
                          {connExpanded ? '▴ Voir moins' : `▾ ${rest} autre${rest > 1 ? 's' : ''} thème${rest > 1 ? 's' : ''}`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}

        <div className="section-title">🚀 Lancer un quizz</div>

        <div className="theme-grid">
          {themes.map((theme, i) => {
            const themeStat = stats.find(s => s.theme_id === theme.id);
            const isLocked = theme.locked && !theme.user_unlocked;
            return (
              <motion.div
                key={theme.id}
                className={`theme-card ${isLocked ? 'theme-locked' : ''}`}
                onClick={() => isLocked ? navigate('/unlock') : startQuiz(theme.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="theme-icon">{theme.icon || THEME_ICONS[theme.slug] || '📖'}</div>
                <div className="theme-name">
                  {theme.name}
                  {isLocked && <span className="theme-lock-badge">🔒</span>}
                </div>
                <div className="theme-desc">{theme.description}</div>
                {!isLocked && (
                  <div className="theme-meta">
                    <span>📝 {theme.question_count} questions</span>
                    {themeStat && themeStat.answered > 0 && (
                      <span>🎯 {themeStat.accuracy}% réussite</span>
                    )}
                    {themeStat && themeStat.current_difficulty && (
                      <span>{DIFF_LABELS[themeStat.current_difficulty]}</span>
                    )}
                    {themeStat && (
                      <span>📚 {themeStat.cards_unlocked}/{themeStat.total_cards} fiches</span>
                    )}
                  </div>
                )}
                {isLocked && (
                  <div className="theme-meta">
                    <span>📝 {theme.question_count} questions</span>
                    <span style={{ color: 'var(--gold)' }}>🔓 Inclus dans la version complète</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {!loading && (
          <div className="ranking-legend" style={{ marginTop: 24 }}>
            <h4>Comment gagner des points ?</h4>
            <div className="redemption-highlight" style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'linear-gradient(90deg, rgba(52,224,196,0.16), rgba(52,224,196,0.04))',
              border: '1px solid rgba(52,224,196,0.3)', borderRadius: 12,
              padding: '12px 14px', margin: '8px 0 14px',
            }}>
              <span style={{ fontSize: '1.6rem' }}>💪</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>Réussir une question ratée avant</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Apprendre de ses erreurs, c'est la plus belle victoire !
                </div>
              </div>
              <span style={{ fontWeight: 800, color: 'var(--gold)', whiteSpace: 'nowrap' }}>+150 pts</span>
            </div>
            <div className="ranking-legend-grid">
              <span>✅ Bonne réponse</span><span>+10 pts</span>
              <span>🔥 Combo x2</span><span>+5 pts</span>
              <span>🔥🔥 Combo x3-4</span><span>+10 pts</span>
              <span>🔥🔥🔥 Combo x5+</span><span>+20 pts</span>
              <span>📚 Nouvelle fiche</span><span>+5 pts</span>
              <span>🔗 Connexion</span><span>+15 pts</span>
              <span>🏁 Quiz terminé</span><span>+10 pts</span>
              <span>👑 Quiz parfait</span><span>+50 pts</span>
            </div>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="loading-rocket">🚀</div>
            <p style={{ color: 'var(--text-dim)', marginTop: 12 }}>Chargement des thèmes...</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
